import React, { ComponentProps, useState } from "react";
import { IonInput, IonTextarea } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import classNames from "classnames/bind";
import styles from "./InputBottomSheet.module.scss";
import { CustomBottomSheet } from "../UI/CustomBottomSheet";
import { ReactComponent as PencilIcon } from "../../assets/icon/pencil.svg";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

type IonInputProps = ComponentProps<typeof IonInput>;
type IonTextareaProps = ComponentProps<typeof IonTextarea>;

interface CommonProps {
  title: string;
  inputPlaceholder: string;
  onFooterSaveClickChange: (value?: any) => void;
  renderPreview?: (val?: string) => React.ReactNode;
  errorMessage?: string;
  disabledPreview?: boolean;
  required?: boolean;
  value?: string;
  defaultPreviewClassName?: string;
}

type InputBottomSheetProps = CommonProps & IonInputProps & IonTextareaProps;

const InputBottomSheet = ({
  title,
  value,
  required,
  disabledPreview,
  inputPlaceholder,
  onFooterSaveClickChange: onChange,
  renderPreview,
  errorMessage,
  defaultPreviewClassName,
  ...inputProps
}: InputBottomSheetProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value ?? "");

  const renderDefaultInputPreview = () => (
    <div className="flex items-center justify-between px-2.5 py-4  min-h-[4.375rem]">
      <div className={cx("flex flex-col", defaultPreviewClassName)}>
        <div className="flex items-center gap-1 text-[#292929]">
          {title}
          {required && <div className="text-xs text-red-500">*</div>}
        </div>
        <div className="text-[length:xl-size] text-[#656565]">{value}</div>
      </div>
      <PencilIcon className="text-sm font-bold w-4 h-4 text-[#D5D5D5]" />
    </div>
  );

  const isIos = Capacitor.getPlatform() === "ios";

  return (
    <CustomBottomSheet
      title={title}
      error={errorMessage}
      disabledPreview={disabledPreview}
      footerTitle={t("Save")}
      onFooterClick={() => onChange(inputValue)}
      renderPreview={renderPreview ?? renderDefaultInputPreview}
      contentClassName={cx("content")}
      snapPoints={({ minHeight }) => [isIos ? minHeight + 50 : minHeight]}
    >
      {inputProps?.rows ? (
        <IonTextarea
          value={inputValue}
          placeholder={inputPlaceholder}
          className="bg-[#F0F0F0] rounded-[10px] p-2.5 pt-0 text-xs"
          {...(inputProps as ComponentProps<typeof IonTextarea>)}
          onIonChange={(e) => setInputValue(e.detail.value || "")}
        />
      ) : (
        <IonInput
          value={inputValue}
          placeholder={inputPlaceholder}
          className="bg-[#F0F0F0] rounded-[10px] !px-2.5 !pt-0 text-xs"
          {...(inputProps as ComponentProps<typeof IonInput>)}
          onIonChange={(e) => setInputValue(e.detail.value || "")}
        />
      )}
    </CustomBottomSheet>
  );
};

export default InputBottomSheet;
